import { VentoryColor } from '../../VentoryUI/util/color.util';
import {
  AssetTransaction,
  AssetTransactionParentType,
  AssetTransactionStatus,
  AssetTransactionType,
} from './assetTransaction';

export namespace AssetTransactionUtil {
  export const DetailedStatus = {
    assetComplete: 'assetComplete',
    removeFromAssets: 'removeFromAssets',
    completedReturn: 'completedReturn',
    completedAssign: 'completedAssign',
    created: 'created',
    pendingReturn: 'pendingReturn',
    pendingAssign: 'pendingAssign',
    ownershipConfirmed: 'ownershipConfirmed',
    pendingOwnershipConfirmation: 'pendingOwnershipConfirmation',
  } as const;

  export type DetailedStatus =
    (typeof AssetTransactionUtil.DetailedStatus)[keyof typeof AssetTransactionUtil.DetailedStatus];

  export const transactionToDetailedStatus = (tx: AssetTransaction) => {
    if (tx.parentType === AssetTransactionParentType.ownership) {
      switch (tx.status) {
        case AssetTransactionStatus.completed:
          return DetailedStatus.ownershipConfirmed;
        case AssetTransactionStatus.created:
          return DetailedStatus.pendingOwnershipConfirmation;
      }
    }

    switch (tx.status) {
      case AssetTransactionStatus.completed:
        if (!tx.location) {
          if (tx.type === AssetTransactionType.inbound) return DetailedStatus.assetComplete;
          if (tx.type === AssetTransactionType.outbound) return DetailedStatus.removeFromAssets;
        }
        return tx.parentType === AssetTransactionParentType.returnAsset
          ? DetailedStatus.completedReturn
          : DetailedStatus.completedAssign;
      case AssetTransactionStatus.created:
        return DetailedStatus.created;
      case AssetTransactionStatus.pending:
        return tx.parentType === AssetTransactionParentType.returnAsset
          ? DetailedStatus.pendingReturn
          : DetailedStatus.pendingAssign;
    }
  };

  export const statusToColorStyle = (asset: AssetTransaction) => {
    if (asset.parentType === AssetTransactionParentType.ownership) {
      switch (asset.status) {
        case AssetTransactionStatus.completed:
          return {
            color: VentoryColor.green700,
            backgroundColor: VentoryColor.green50,
          };
        case AssetTransactionStatus.created:
          return {
            color: VentoryColor.yellow700,
            backgroundColor: VentoryColor.yellow50,
          };
      }
    }

    switch (asset.status) {
      case AssetTransactionStatus.completed:
        return {
          color: VentoryColor.green700,
          backgroundColor: VentoryColor.green50,
        };
      case AssetTransactionStatus.created:
        return {
          color: VentoryColor.blue700,
          backgroundColor: VentoryColor.blue50,
        };
      case AssetTransactionStatus.pending:
        return {
          color: VentoryColor.yellow700,
          backgroundColor: VentoryColor.yellow50,
        };
    }
  };

  export const groupByParent = (transactions: AssetTransaction[]): AssetTransaction[] => {
    const groupedByParent = new Map<string, AssetTransaction>();

    for (const transaction of transactions) {
      if (transaction.parentType === AssetTransactionParentType.ownership) {
        groupedByParent.set(transaction.id, transaction);
        continue;
      }

      const existing = groupedByParent.get(transaction.parentId);
      if (!existing) {
        groupedByParent.set(transaction.parentId, transaction);
        continue;
      }

      if (existing && existing.status === AssetTransactionStatus.completed && transaction.status !== existing.status) {
        groupedByParent.set(transaction.parentId, transaction);
      } else if (
        existing &&
        existing.parentType === AssetTransactionParentType.reassign &&
        existing.type === AssetTransactionType.outbound
      ) {
        groupedByParent.set(transaction.parentId, transaction);
      } else if (existing && !existing.location && transaction.location) {
        groupedByParent.set(transaction.parentId, transaction);
      }
    }
    return [...groupedByParent.values()];
  };
}
